/**
 * <welcome-popup> — the "פופאפ כניסה לאתר" section (sections/welcome-popup.liquid).
 *
 * Opens a native <dialog> once a visitor lands on the site, after a delay,
 * respecting a frequency rule (every page / once per visit / every N days)
 * and per-device toggles. In the theme editor it opens while the section is
 * selected (or permanently with "editor preview") and never touches storage.
 */
const STORAGE_KEY = 'welcome-popup:seen';
// Session cookie (no expiry): shared by every tab, cleared when the browser closes.
const SESSION_COOKIE = 'welcome_popup_seen';
const DESKTOP_QUERY = '(min-width: 750px)';

class WelcomePopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.sectionId = this.dataset.sectionId;
    this.onClick = this.onClick.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.onDeselect = this.onDeselect.bind(this);

    this.dialog.addEventListener('click', this.onClick);
    this.dialog.addEventListener('cancel', this.onCancel);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);
      if (this.dataset.editorPreview === 'true') this.open();
      return;
    }

    if (this.dataset.enabled !== 'true') return;

    // Returning from a newsletter submit (success or error): show the result now.
    if (this.querySelector('[data-welcome-popup-force-open]')) {
      this.open();
      return;
    }

    if (!this.shouldShow()) return;

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.timer = window.setTimeout(() => {
      this.open();
      this.markSeen();
    }, delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    document.removeEventListener('shopify:section:select', this.onSelect);
    document.removeEventListener('shopify:section:deselect', this.onDeselect);
    this.unlockScroll();
  }

  shouldShow() {
    const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
    if (isDesktop && this.dataset.showDesktop !== 'true') return false;
    if (!isDesktop && this.dataset.showMobile !== 'true') return false;

    switch (this.dataset.frequency) {
      case 'always':
        return true;
      case 'session':
        return !document.cookie.split('; ').some((c) => c.startsWith(`${SESSION_COOKIE}=`));
      default: {
        const seenAt = Number(readStorage(localStorage));
        if (!seenAt) return true;
        const days = Math.max(1, Number(this.dataset.days) || 1);
        return Date.now() - seenAt > days * 24 * 60 * 60 * 1000;
      }
    }
  }

  markSeen() {
    if (this.dataset.frequency === 'session') {
      document.cookie = `${SESSION_COOKIE}=1; path=/; SameSite=Lax`;
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Storage blocked (private mode etc.) — popup simply shows again next time.
    }
  }

  open() {
    if (this.dialog.open) return;
    this.dialog.classList.remove('is-closing');
    this.dialog.showModal();
    this.lockScroll();
  }

  close() {
    if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
    this.dialog.classList.add('is-closing');

    const finish = () => {
      this.dialog.classList.remove('is-closing');
      this.dialog.close();
      this.unlockScroll();
    };

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      finish();
    } else {
      this.dialog.addEventListener('animationend', finish, { once: true });
      // Fallback in case the animation never fires (e.g. stale CSS bundle).
      window.setTimeout(() => this.dialog.open && finish(), 300);
    }
  }

  onClick(event) {
    if (event.target.closest('[data-welcome-popup-close]')) {
      this.close();
      return;
    }

    if (event.target.closest('[data-welcome-popup-cta]')) {
      this.markSeen();
      this.unlockScroll();
      return;
    }

    // A click on the <dialog> itself (not its content) is a click on the backdrop.
    if (event.target === this.dialog && this.dataset.closeOnOverlay === 'true') {
      this.close();
    }
  }

  onCancel(event) {
    // ESC: run our closing animation instead of the instant native close.
    event.preventDefault();
    this.close();
  }

  onSelect(event) {
    if (event.detail?.sectionId === this.sectionId) this.open();
  }

  onDeselect(event) {
    if (event.detail?.sectionId === this.sectionId && this.dataset.editorPreview !== 'true') this.close();
  }

  lockScroll() {
    document.documentElement.style.overflow = 'hidden';
  }

  unlockScroll() {
    document.documentElement.style.removeProperty('overflow');
  }
}

function readStorage(store) {
  try {
    return store.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

if (!customElements.get('welcome-popup')) {
  customElements.define('welcome-popup', WelcomePopup);
}
